// src/services/ContentService.js
// Production service for creator content (photos & collections)
// Backed by Supabase tables, Storage bucket, and LunexxCollections smart contract

import { supabase } from '../lib/supabase';
import { authService } from './AuthService';

// ---------------------------------------------------------------------------
// EVERY WRITE GOES THROUGH THE SERVER.
//
// collections / collection_photos / featured_photos used to be written
// straight from the browser with the public anon key, and their row policies
// allowed anyone to do it -- so a stranger could retitle, reprice or delete
// any creator's collection (and sign-collection-purchase signs its voucher at
// collections.price_eth). The creator-content function verifies the SIWE
// token and that the caller OWNS the creator before touching a row.
//
// Images are still uploaded and deleted from the public bucket by the client;
// the server hands back `orphanedUrls` for whatever a write left behind.
// ---------------------------------------------------------------------------
async function contentBridge(body) {
  const authToken = authService.getToken();
  if (!authToken) {
    throw new Error('Sign in with your wallet to manage content');
  }
  const { data, error } = await supabase.functions.invoke('creator-content', {
    body,
    headers: { 'X-Lunexx-Auth': `Bearer ${authToken}` },
  });
  if (error) throw new Error(data?.error || error.message || 'Request failed');
  if (data?.error) throw new Error(data.error);
  return data;
}

const STORAGE_BUCKET = 'creator-content';
const MEDIA_BUCKET = 'collection-media';

/** Reads the gated photo list. Auth is OPTIONAL: signed out you get the tiny
 *  previews, signed in as a buyer (or the creator) you get short-lived signed
 *  urls for the real images. The private object paths never reach us. */
async function mediaBridge(collectionIds) {
  const ids = (collectionIds || []).filter(Boolean);
  if (!ids.length) return {};
  const authToken = authService.getToken();
  const { data, error } = await supabase.functions.invoke('creator-content', {
    body: { action: 'collection_media', collectionIds: ids },
    headers: authToken ? { 'X-Lunexx-Auth': `Bearer ${authToken}` } : undefined,
  });
  if (error || data?.error) {
    console.error('[ContentService] collection_media failed:', data?.error || error);
    return {};
  }
  return data?.collections || {};
}

/** A deliberately tiny thumbnail for the LOCKED grid. ~40px wide, then blurred
 *  in CSS: enough to hint at the photo, worthless as the photo. Generated in
 *  the browser so the paid original is the only thing that ever leaves at full
 *  size, and it goes to the PUBLIC bucket because it is meant to be public. */
async function buildPreviewBlob(blob) {
  const bitmap = await createImageBitmap(blob);
  const w = 40;
  const h = Math.max(1, Math.round((bitmap.height / bitmap.width) * w));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  return await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.5));
}

class ContentService {
  // ============================================
  // STORAGE: Upload base64/Blob/File to Supabase Storage
  // ============================================

  /** data: url | File | Blob -> Blob. One place, used by both the public
   *  uploader and the private paid-photo uploader. */
  async _toBlob(imageData) {
    if (typeof imageData === 'string' && imageData.startsWith('data:')) {
      const [header, base64Data] = imageData.split(',');
      const mimeMatch = header.match(/data:([^;]+)/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
    }
    if (imageData instanceof File || imageData instanceof Blob) return imageData;
    throw new Error('Unsupported image input');
  }

  async _uploadImage(imageData, folder = 'misc') {
    if (!imageData) return null;

    if (typeof imageData === 'string' && /^https?:\/\//i.test(imageData)) {
      return imageData;
    }

    const blob = await this._toBlob(imageData);
    const extension = (imageData instanceof File
      ? (imageData.name.split('.').pop() || 'jpg')
      : (blob.type.split('/')[1] || 'jpg')).toLowerCase();

    const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).substring(2, 10)}.${extension}`;

    const { error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(fileName, blob, {
        cacheControl: '3600',
        upsert: false,
        contentType: blob.type,
      });

    if (error) {
      console.error('[ContentService] Upload failed:', error);
      throw new Error(`Failed to upload image: ${error.message}`);
    }

    const { data } = supabase.storage
      .from(STORAGE_BUCKET)
      .getPublicUrl(fileName);

    return data.publicUrl;
  }

  /** Upload a paid collection photo: the ORIGINAL into the private bucket
   *  through a one-shot signed PUT, and a tiny preview into the public one.
   *  Returns what the row needs; the private path is echoed back only so the
   *  row can be written, and is never rendered. */
  async _uploadCollectionPhoto(imageData, creatorId, collectionId) {
    const blob = await this._toBlob(imageData);
    const extension = (blob.type.split('/')[1] || 'jpg').toLowerCase();

    const { path, token } = await contentBridge({
      action: 'photo_upload_url', creatorId, collectionId, ext: extension,
    });
    const { error } = await supabase.storage
      .from(MEDIA_BUCKET)
      .uploadToSignedUrl(path, token, blob, { contentType: blob.type });
    if (error) throw new Error(`Failed to upload photo: ${error.message}`);

    let previewUrl = null;
    try {
      const preview = await buildPreviewBlob(blob);
      if (preview) previewUrl = await this._uploadImage(preview, `collections/${creatorId}/previews`);
    } catch (err) {
      // A missing preview is cosmetic — the locked tile falls back to the
      // collection banner. Never fail the upload over it.
      console.warn('[ContentService] preview build failed:', err);
    }

    return { lockedPath: path, previewUrl };
  }

  async _deleteImage(publicUrl) {
    if (!publicUrl || typeof publicUrl !== 'string') return;
    if (!publicUrl.includes(STORAGE_BUCKET)) return;

    try {
      const parts = publicUrl.split(`/${STORAGE_BUCKET}/`);
      if (parts.length < 2) return;
      const path = parts[1].split('?')[0];

      await supabase.storage.from(STORAGE_BUCKET).remove([path]);
    } catch (err) {
      console.warn('[ContentService] Failed to delete image:', err);
    }
  }

  // ============================================
  // FEATURED PHOTOS
  // ============================================

  async getFeaturedPhotos(creatorId) {
    if (!creatorId) return [];

    const { data, error } = await supabase
      .from('featured_photos')
      .select('*')
      .eq('creator_id', creatorId)
      .order('position', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[ContentService] getFeaturedPhotos error:', error);
      return [];
    }

    return (data || []).map(p => ({
      id: p.id,
      creatorId: p.creator_id,
      url: p.url,
      isPublic: p.is_public,
      position: p.position,
    }));
  }

  async addFeaturedPhoto(creatorId, photoData) {
    if (!creatorId) throw new Error('creatorId is required');

    const uploadedUrl = await this._uploadImage(photoData.url, `featured/${creatorId}`);
    try {
      const { photo } = await contentBridge({
        action: 'featured_add',
        creatorId,
        url: uploadedUrl,
        isPublic: photoData.isPublic ?? true,
      });
      return {
        id: photo.id,
        creatorId: photo.creator_id,
        url: photo.url,
        isPublic: photo.is_public,
        position: photo.position,
      };
    } catch (err) {
      await this._deleteImage(uploadedUrl);
      throw err;
    }
  }

  async updateFeaturedPhoto(creatorId, photoId, updates) {
    if (!creatorId || !photoId) throw new Error('creatorId and photoId are required');

    const payload = { action: 'featured_update', creatorId, photoId };
    if (updates.url !== undefined) {
      payload.url = await this._uploadImage(updates.url, `featured/${creatorId}`);
    }
    if (updates.isPublic !== undefined) payload.isPublic = updates.isPublic;
    if (updates.position !== undefined) payload.position = updates.position;

    const { photo, orphanedUrls } = await contentBridge(payload);
    await Promise.all((orphanedUrls || []).map((url) => this._deleteImage(url)));

    return {
      id: photo.id,
      creatorId: photo.creator_id,
      url: photo.url,
      isPublic: photo.is_public,
      position: photo.position,
    };
  }

  async deleteFeaturedPhoto(creatorId, photoId) {
    if (!creatorId || !photoId) throw new Error('creatorId and photoId are required');

    const { orphanedUrls } = await contentBridge({
      action: 'featured_delete', creatorId, photoId,
    });
    await Promise.all((orphanedUrls || []).map((url) => this._deleteImage(url)));
    return true;
  }

  async togglePhotoVisibility(creatorId, photoId) {
    if (!creatorId || !photoId) throw new Error('creatorId and photoId are required');

    const { photo } = await contentBridge({
      action: 'featured_toggle', creatorId, photoId,
    });
    return {
      id: photo.id,
      creatorId: photo.creator_id,
      url: photo.url,
      isPublic: photo.is_public,
      position: photo.position,
    };
  }

  // ============================================
  // COLLECTIONS
  // ============================================

  async getCollections(creatorId) {
    if (!creatorId) return [];

    const { data: collections, error } = await supabase
      .from('collections')
      .select('*')
      .eq('creator_id', creatorId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[ContentService] getCollections error:', error);
      return [];
    }

    if (!collections || collections.length === 0) return [];

    const collectionIds = collections.map(c => c.id);
    // Photos come from the gated endpoint, never straight from the table: a
    // locked collection must not hand its urls to the browser.
    const media = await mediaBridge(collectionIds);
    const photosByCollection = {};
    collectionIds.forEach((id) => {
      photosByCollection[id] = (media[id]?.photos || []).map((p) => ({
        id: p.id,
        url: p.url,
        previewUrl: p.previewUrl,
        locked: p.locked,
        description: p.description,
        position: p.position,
      }));
    });

    // Pull cached sales counts AND revenue from collection_purchases
    const { data: purchaseStats } = await supabase
      .from('collection_purchases')
      .select('collection_id, amount_eth, creator_amount_eth, protocol_fee_eth')
      .in('collection_id', collectionIds);

    const statsByCollection = {};
    (purchaseStats || []).forEach(p => {
      if (!statsByCollection[p.collection_id]) {
        statsByCollection[p.collection_id] = {
          sales: 0,
          totalRevenue: 0,
          creatorEarnings: 0,
          protocolFees: 0,
        };
      }
      const s = statsByCollection[p.collection_id];
      s.sales += 1;
      s.totalRevenue += parseFloat(p.amount_eth) || 0;
      s.creatorEarnings += parseFloat(p.creator_amount_eth) || 0;
      s.protocolFees += parseFloat(p.protocol_fee_eth) || 0;
    });

    return collections.map((c, idx) => {
      const stats = statsByCollection[c.id] || { sales: 0, totalRevenue: 0, creatorEarnings: 0, protocolFees: 0 };
      return {
        id: c.id,
        creatorId: c.creator_id,
        title: c.title,
        description: c.description || '',
        bannerUrl: c.banner_url,
        city: c.location,
        location: c.location,
        price: parseFloat(c.price_eth) || 0,
        // collections are unlocked with the Lunexx balance at a USD-cents
        // price; a legacy ETH price is 'unpriced' (no longer for sale)
        priceCents: c.price_cents ?? null,
        priceRevision: c.price_revision ?? 0,
        accessClass: collectionAccessClass(c),
        purchaseCount: stats.sales,
        totalRevenue: stats.totalRevenue,
        creatorEarnings: stats.creatorEarnings,
        protocolFees: stats.protocolFees,
        chainListed: c.chain_listed || false,
        // Server decision (creator-content collection_media): `free` = open
        // to any signed-in user (Phase-1 launch collection); `entitled` =
        // this caller gets full media now.
        free: media[c.id]?.free === true,
        entitled: media[c.id]?.entitled === true,
        listingTxHash: c.listing_tx_hash,
        listedAt: c.listed_at,
        series: c.series || String(idx + 1).padStart(2, '0'),
        createdAt: c.created_at,
        updatedAt: c.updated_at,
        photos: photosByCollection[c.id] || [],
      };
    });
  }

  async getCollectionById(creatorId, collectionId) {
    if (!creatorId || !collectionId) return null;

    const { data: c, error } = await supabase
      .from('collections')
      .select('*')
      .eq('id', collectionId)
      .eq('creator_id', creatorId)
      .single();

    if (error || !c) {
      console.error('[ContentService] getCollectionById error:', error);
      return null;
    }

    const media = await mediaBridge([collectionId]);
    const photos = media[collectionId]?.photos || [];

    const { data: purchases } = await supabase
      .from('collection_purchases')
      .select('amount_eth, creator_amount_eth, protocol_fee_eth')
      .eq('collection_id', collectionId);

    const stats = (purchases || []).reduce((acc, p) => {
      acc.sales += 1;
      acc.totalRevenue += parseFloat(p.amount_eth) || 0;
      acc.creatorEarnings += parseFloat(p.creator_amount_eth) || 0;
      acc.protocolFees += parseFloat(p.protocol_fee_eth) || 0;
      return acc;
    }, { sales: 0, totalRevenue: 0, creatorEarnings: 0, protocolFees: 0 });

    return {
      id: c.id,
      creatorId: c.creator_id,
      title: c.title,
      description: c.description || '',
      bannerUrl: c.banner_url,
      city: c.location,
      location: c.location,
      price: parseFloat(c.price_eth) || 0,
      priceCents: c.price_cents ?? null,
      priceRevision: c.price_revision ?? 0,
      accessClass: collectionAccessClass(c),
      purchaseCount: stats.sales,
      totalRevenue: stats.totalRevenue,
      creatorEarnings: stats.creatorEarnings,
      protocolFees: stats.protocolFees,
      chainListed: c.chain_listed || false,
      listingTxHash: c.listing_tx_hash,
      listedAt: c.listed_at,
      series: c.series,
      createdAt: c.created_at,
      updatedAt: c.updated_at,
      photos: (photos || []).map(p => ({
        id: p.id,
        url: p.url,
        previewUrl: p.previewUrl,
        locked: p.locked,
        description: p.description,
        position: p.position,
      })),
    };
  }

  async createCollection(creatorId, collectionData) {
    if (!creatorId) throw new Error('creatorId is required');

    const bannerUrl = collectionData.bannerUrl
      ? await this._uploadImage(collectionData.bannerUrl, `collections/${creatorId}/banners`)
      : null;

    let created;
    try {
      const { collection } = await contentBridge({
        action: 'collection_create',
        creatorId,
        title: collectionData.title,
        description: collectionData.description || '',
        bannerUrl,
        location: collectionData.city || collectionData.location || null,
        ...(collectionData.priceCents != null ? { priceCents: collectionData.priceCents } : {}),
      });
      // the server creates it priced or not at all; without a collection id
      // nothing is uploaded or published
      if (!collection?.id) throw new Error('Could not create the collection.');
      created = collection;
    } catch (err) {
      if (bannerUrl) await this._deleteImage(bannerUrl);
      throw err;
    }

    const photos = collectionData.photos || [];
    if (photos.length > 0) {
      await this._upsertCollectionPhotos(created.id, photos, creatorId);
    }

    return await this.getCollectionById(creatorId, created.id);
  }

  async updateCollection(creatorId, collectionId, updates) {
    if (!creatorId || !collectionId) throw new Error('creatorId and collectionId required');

    const payload = { action: 'collection_update', creatorId, collectionId };

    if (updates.bannerUrl !== undefined) {
      payload.bannerUrl = updates.bannerUrl
        ? await this._uploadImage(updates.bannerUrl, `collections/${creatorId}/banners`)
        : null;
    }
    if (updates.title !== undefined) payload.title = updates.title;
    if (updates.description !== undefined) payload.description = updates.description;
    if (updates.city !== undefined) payload.location = updates.city;
    if (updates.location !== undefined) payload.location = updates.location;
    if (updates.priceCents !== undefined) payload.priceCents = updates.priceCents;

    const { orphanedUrls } = await contentBridge(payload);

    if (updates.photos !== undefined) {
      await this._upsertCollectionPhotos(collectionId, updates.photos, creatorId);
    }
    await Promise.all((orphanedUrls || []).map((url) => this._deleteImage(url)));

    return await this.getCollectionById(creatorId, collectionId);
  }

  async deleteCollection(creatorId, collectionId) {
    if (!creatorId || !collectionId) throw new Error('creatorId and collectionId required');

    const { orphanedUrls } = await contentBridge({
      action: 'collection_delete', creatorId, collectionId,
    });
    await Promise.all((orphanedUrls || []).map((url) => this._deleteImage(url)));
    return true;
  }

  // ============================================
  // CHAIN STATE TRACKING
  // ============================================

  /**
   * Mark a collection as listed on-chain after a successful listCollection tx.
   */
  async markChainListed(creatorId, collectionId, txHash) {
    if (!creatorId || !collectionId || !txHash) {
      throw new Error('creatorId, collectionId and txHash required');
    }
    await contentBridge({
      action: 'collection_mark_listed', creatorId, collectionId, txHash,
    });
    return true;
  }

  // ============================================
  // PURCHASES (chain-backed cache)
  // ============================================

  /**
   * Record a purchase, SERVER-SIDE.
   *
   * This used to insert into collection_purchases straight from the browser,
   * under an RLS policy of WITH CHECK (true): every column was a client
   * assertion, so anyone holding the publishable key could write any
   * collection, any wallet, any tx_hash and any amount and have it stand as
   * a sale. The amounts fed the revenue figures on every collection card.
   *
   * The browser now supplies one thing: a transaction hash. The
   * sign-collection-purchase function proves the payment and reads the
   * split out of the contract's own CollectionPurchased log.
   */
  async recordPurchase({ collectionId, txHash, authToken }) {
    if (!collectionId || !txHash) {
      throw new Error('collectionId and txHash required');
    }
    if (!authToken) {
      throw new Error('authToken required \u2014 purchases are recorded against the signed-in wallet');
    }

    const { data, error } = await supabase.functions.invoke('sign-collection-purchase', {
      body: { action: 'record', collectionId, txHash },
      headers: { 'X-Lunexx-Auth': `Bearer ${authToken}` },
    });

    if (error) {
      let message = error.message || 'Failed to record purchase';
      if (error.context?.json) {
        try { const d = await error.context.json(); if (d?.error) message = d.error; } catch { /* noop */ }
      }
      // already_settled / already_owned are successes from the caller's
      // point of view: the row the caller wanted exists.
      if (/already_settled|already_owned/i.test(message)) return null;
      console.error('[ContentService] recordPurchase error:', message);
      throw new Error(message);
    }

    return data ?? null;
  }

  /**
   * Get all collections a wallet has purchased.
   * Returns array of collection IDs.
   */
  async getUserPurchases(buyerWallet) {
    if (!buyerWallet) return [];

    const { data, error } = await supabase
      .from('collection_purchases')
      .select('collection_id')
      .eq('buyer_wallet', buyerWallet.toLowerCase());

    if (error) {
      console.error('[ContentService] getUserPurchases error:', error);
      return [];
    }

    return (data || []).map(p => p.collection_id);
  }

  /**
   * Check if a specific wallet has purchased a specific collection (DB cache check).
   * Use chain check for canonical truth.
   */
  async hasUserPurchased(collectionId, buyerWallet) {
    if (!collectionId || !buyerWallet) return false;

    const { count } = await supabase
      .from('collection_purchases')
      .select('id', { count: 'exact', head: true })
      .eq('collection_id', collectionId)
      .eq('buyer_wallet', buyerWallet.toLowerCase());

    return (count || 0) > 0;
  }

  // ============================================
  // COLLECTION PHOTOS
  // ============================================

  async _upsertCollectionPhotos(collectionId, incomingPhotos, creatorId) {
    // Upload any new images FIRST so the server only ever sees https urls,
    // then let it diff the rows in one call and tell us what to clean up.
    const photos = [];
    for (let i = 0; i < incomingPhotos.length; i++) {
      const photo = incomingPhotos[i] || {};
      const entry = { id: photo.id, description: photo.description || '', position: i };
      const isNewImage = photo.url && (typeof photo.url !== 'string' || !/^https?:\/\//i.test(photo.url));
      if (isNewImage) {
        const { lockedPath, previewUrl } = await this._uploadCollectionPhoto(
          photo.url, creatorId, collectionId,
        );
        entry.lockedPath = lockedPath;
        entry.previewUrl = previewUrl;
      } else if (!photo.id) {
        // An existing https url on a NEW row (an external image): keep it as
        // a plain public url. An existing ROW without a new image sends
        // neither field, and the server keeps what it already has -- signed
        // urls must never be written back as the stored url.
        if (typeof photo.url === 'string') entry.url = photo.url;
        else continue;
      }
      photos.push(entry);
    }

    const { orphanedUrls } = await contentBridge({
      action: 'photos_sync', creatorId, collectionId, photos,
    });
    await Promise.all((orphanedUrls || []).map((url) => this._deleteImage(url)));
  }

  async addPhotoToCollection(creatorId, collectionId, photoData) {
    if (!collectionId) throw new Error('collectionId required');

    const { lockedPath, previewUrl } = await this._uploadCollectionPhoto(
      photoData.url, creatorId, collectionId,
    );
    try {
      const { photo } = await contentBridge({
        action: 'photo_add',
        creatorId,
        collectionId,
        lockedPath,
        previewUrl,
        description: photoData.description || '',
      });
      return {
        id: photo.id, url: photo.url, previewUrl: photo.preview_url,
        description: photo.description, position: photo.position,
      };
    } catch (err) {
      if (previewUrl) await this._deleteImage(previewUrl);
      throw err;
    }
  }

  async updateCollectionPhoto(creatorId, collectionId, photoId, updates) {
    if (!photoId) throw new Error('photoId required');

    const payload = { action: 'photo_update', creatorId, collectionId, photoId };
    if (updates.url !== undefined) {
      const { lockedPath, previewUrl } = await this._uploadCollectionPhoto(
        updates.url, creatorId, collectionId,
      );
      payload.lockedPath = lockedPath;
      payload.previewUrl = previewUrl;
      payload.url = null; // the public copy is gone; the private one is the photo
    }
    if (updates.description !== undefined) payload.description = updates.description;
    if (updates.position !== undefined) payload.position = updates.position;

    const { photo, orphanedUrls } = await contentBridge(payload);
    await Promise.all((orphanedUrls || []).map((url) => this._deleteImage(url)));

    return {
      id: photo.id, url: photo.url, previewUrl: photo.preview_url,
      description: photo.description, position: photo.position,
    };
  }

  async deleteCollectionPhoto(creatorId, collectionId, photoId) {
    if (!photoId) throw new Error('photoId required');

    const { orphanedUrls } = await contentBridge({
      action: 'photo_delete', creatorId, collectionId, photoId,
    });
    await Promise.all((orphanedUrls || []).map((url) => this._deleteImage(url)));
    return true;
  }

  async reorderCollectionPhotos(creatorId, collectionId, photoIds) {
    if (!collectionId || !Array.isArray(photoIds)) {
      throw new Error('collectionId and photoIds array required');
    }
    const { photos } = await contentBridge({
      action: 'photos_reorder', creatorId, collectionId, photoIds,
    });
    return (photos || []).map((p) => ({
      id: p.id, url: p.url, description: p.description, position: p.position,
    }));
  }

  // ============================================
  // PUBLIC API (for PostFeed)
  // ============================================

  async getPublicContent(creatorId) {
    if (!creatorId) return { photos: [], collections: [] };

    const [photos, collections] = await Promise.all([
      this.getFeaturedPhotos(creatorId),
      this.getCollections(creatorId),
    ]);

    return { photos, collections };
  }
}

/** Mirror of SQL collection_access_class (the server decides; this only labels). */
export function collectionAccessClass(c) {
  if (!c) return 'free';
  if (Number(c.price_cents || 0) > 0) return 'priced';
  if (c.price_cents === 0 || c.price_cents === '0') return 'free';   // explicitly free
  if (Number(c.price_eth || 0) > 0) return 'unpriced';
  return 'free';
}

export const contentService = new ContentService();
export default contentService;